import { Box, List, ListItem, ListItemButton, Chip, Radio, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { tpTheme } from "@trainingpeaks/designtokens";

export interface Option {
  label: string;
  value: "annual" | "monthly";
  originalPrice: string | null;
  currentPrice: string;
  labelSubText: string | null;
  isMostPopular: boolean;
}

interface PremiumUpgradeComponentOptionsListProps {
  options: Option[];
  selected: "annual" | "monthly";
  setSelected: (value: "annual" | "monthly") => void;
}

const PremiumUpgradeComponentOptionsList = ({
  options,
  selected,
  setSelected
}: PremiumUpgradeComponentOptionsListProps) => {
  const { t } = useTranslation();
  return (
    <List className="options flex flex-col w-full gap-3 mb-4" data-testid="upgrade-checkout-options-list">
      {options.map((option) => {
        const isSelected = selected === option.value;
        return (
          <ListItem
            key={option.value}
            disablePadding
            className={`relative h-16 transition-all ${
              isSelected ? "bg-blue-900" : "bg-white"
            }`}
            // sx required: border color and radius use design token values unavailable as Tailwind classes
            sx={{
              borderRadius: tpTheme.baseLayer.borderRadius.md,
              border: isSelected
                ? `2px solid ${tpTheme.semanticLayer.colors.primary.light}`
                : `2px solid ${tpTheme.semanticLayer.colors.divider.main}`,
            }}
          >
            {option.isMostPopular && (
              <Box className="absolute right-4 top-0 -translate-y-1/2 z-10">
                <Chip
                  label={t("Most Popular")}
                  className="rounded-xl font-normal"
                  size="small"
                  sx={{
                    backgroundColor: tpTheme.semanticLayer.colors.primary.main,
                    color: "white",
                    fontSize: tpTheme.baseLayer.fontSize.sm,
                    fontWeight: tpTheme.baseLayer.fontWeight[400]
                  }}
                />
              </Box>
            )}
            <ListItemButton
              onClick={() => setSelected(option.value)}
              className="flex items-center justify-between p-4 w-full gap-3"
            >
              <Box className="flex w-1/4 justify-between">
                <Radio
                  checked={isSelected}
                  value={option.value}
                  name="subscription-options"
                  size="small"
                  sx={{
                    padding: 0,
                    color: isSelected ? "white" : "inherit",
                    "&.Mui-checked": {
                      color: tpTheme.semanticLayer.colors.primary.light
                    },
                  }}
                />
                <Box className="w-3/4">
                  <Typography
                    variant="h4"
                    className="font-bold"
                    color={isSelected ? "white" : "text.primary"}
                  >
                    {t(option.label)}
                  </Typography>
                  {option.labelSubText && (
                    <Typography
                      variant="body1"
                      className="text-base"
                      color={isSelected ? "white" : "text.primary"}
                    >
                      {t(option.labelSubText)}
                    </Typography>
                  )}
                </Box>
              </Box>
              <Box className="flex items-center gap-2">
                {option.originalPrice && (
                  <Typography
                    variant="body1"
                    className="line-through"
                    color={isSelected ? "white" : "text.secondary"}
                  >
                    {option.originalPrice}
                  </Typography>
                )}
                <Typography
                  variant="body1"
                  color={isSelected ? "white" : "text.primary"}
                >
                  {option.currentPrice}
                </Typography>
              </Box>
            </ListItemButton>
          </ListItem>
        );
      })}
    </List>
  );
};

export default PremiumUpgradeComponentOptionsList;
